import { useEffect } from 'react'
import { classes, style } from 'typestyle'
import { useParams } from 'react-router-dom'

/* Contents ===================================================================================== */
import { GLOBAL_CONTENTS } from '../../../common/global-contents'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { withRouter } from '../../../contexts/with-router'

/* Hooks ======================================================================================== */
import useLocal from '../../../hooks/use-local'
import useFetch from '../../../hooks/use-fetch'
import useValue from '../../../hooks/use-value'

/* Components =================================================================================== */
import Skeleton from '../../../components/skeleton'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Loader } from '../../../components/loader'
import { Snippet, SnippetHead, SnippetBody } from '../../../modules/layouts/snippet'
import { Text } from '../../../components/text/text.component'

/* Styles ======================================================================================= */
import { mr4 } from '../../../styles/styleset/margin/mr4'
import { pl4 } from '../../../styles/styleset/padding/pl4'
import { pr4 } from '../../../styles/styleset/padding/pr4'
import { mb4 } from '../../../styles/styleset/margin/mb4'
import { f18i } from '../../../styles/styleset/font-size/f18i'
import { mt12 } from '../../../styles/styleset/margin/mt12'
import { mb16 } from '../../../styles/styleset/margin/mb16'

const today = new Date()
const yesterday = new Date(today)
yesterday.setDate(today.getDate() - 1)

const parseDateQuery = (date: any) => {
  const fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
  const toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0)

  return {
    from: {
      key: 'createdAt',
      value: fromDate.toISOString(),
    },
    to: {
      key: 'createdAt',
      value: toDate.toISOString(),
    },
  }
}

export default addSnippet(
  'ParentOverview',
  withRouter((props: any) => {
    const { title, include = [] } = props
    const { groupId } = useParams()

    const [local] = useLocal()
    const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/parent-overview/${groupId}`
    const [refDate, $refDate] = useValue(new Date())
    const [overview, $overview] = useFetch([], {
      url: apiUrl,
      search: {
        date: parseDateQuery(refDate),
        include,
      },
    })

    useEffect(() => {
      if (overview.mounted) {
        $overview.get({
          url: apiUrl,
          search: {
            date: parseDateQuery(refDate),
            include,
          },
        })
      }
    }, [overview.mounted, refDate])

    const dataByDate: any[] = overview.payload
    const sorted =
      dataByDate.length > 0
        ? [...dataByDate[0].data].sort((a: any, b: any) => {
            const countA = a.count === '-' ? 0 : a.count
            const countB = b.count === '-' ? 0 : b.count
            return countB - countA
          })
        : []

    let totalRows = 0

    sorted.map((a: any) => {
      totalRows += a.count === '-' ? 0 : a.count
    })

    return (
      <Snippet>
        <SnippetHead title={GLOBAL_CONTENTS.overview[local]}>
          <Box baseline="none" flex>
            {
              <Button
                baseline={today.toDateString() == refDate.toDateString() ? 'disabled' : 'surface'}
                alt={today.toDateString() == refDate.toDateString()}
                className={mr4}
                size="sm"
                icon={<span className={classes('material-icons', f18i)}>keyboard_arrow_left</span>}
                onClick={() => {
                  $refDate.set((prev: any) => {
                    const newDate = new Date(prev)
                    newDate.setDate(newDate.getDate() + 1)

                    return newDate
                  })
                }}
              />
            }
            <Button
              baseline={'surface'}
              icon={<i className={classes('material-icons', f18i)}>keyboard_arrow_right</i>}
              size="sm"
              onClick={() => {
                $refDate.set((prev: any) => {
                  const newDate = new Date(prev)
                  newDate.setDate(newDate.getDate() - 1)

                  return newDate
                })
              }}
            />
          </Box>
        </SnippetHead>
        <SnippetBody>
          <Loader
            loaded={overview.mounted && !overview.meta.isLoading}
            timeout={300}
            skeleton={
              <>
                <Skeleton width={80} height={16} className={mb4} />
                <Skeleton width={'100%'} height={38} />
                <Skeleton width={80} height={16} className={classes(mb4, mt12)} />
                <Skeleton width={'100%'} height={38} />
                <Skeleton width={80} height={16} className={classes(mb4, mt12)} />
                <Skeleton width={'100%'} height={38} />
              </>
            }
            component={dataByDate.map((date: any, key: any) => {
              return (
                <Box key={key} className={classes(style({ overflow: 'hidden' }))} row rounded>
                  <Box baseline="none" row>
                    {date.data.map((item: any, key: any) => {
                      const count = item.count === '-' ? 0 : item.count
                      return (
                        <Box
                          className={classes(mb16)}
                          baseline="none"
                          style={{
                            transition: '0.2s width ease-in-out',
                            transitionDelay: '2s',
                          }}
                          key={key}
                          row
                        >
                          <Box spaceBetween>
                            <Text size="sm" className={mb4} shrink>
                              {item.label}
                            </Text>
                          </Box>
                          <Box
                            style={{ overflow: 'hidden', height: 38 }}
                            flex
                            baseline="none"
                            rounded
                          >
                            <Box baseline="background" fullWidth relative>
                              <Box
                                style={{
                                  height: '100%',
                                  width: overview.meta.isLoadingDelayed
                                    ? '0px'
                                    : totalRows == 0
                                    ? '0px'
                                    : `${(count / totalRows) * 100}%`,
                                  background: 'rgb(101 153 218)',
                                  borderRadius: key === item.count - 1 ? '0 4px 4px 0' : 0,
                                  transition: '0.1s ease-in-out all',
                                }}
                                className={classes(
                                  !overview.meta.isLoadingDelayed && count && pl4,
                                  !overview.meta.isLoadingDelayed && count && pr4,
                                )}
                                alignRight
                                alignCenterY
                              />
                              <Box
                                absolute
                                centerAbsoluteX
                                centerAbsoluteY
                                className={style({ height: 38, width: '100%' })}
                                flex
                                alignCenterX
                                alignCenterY
                                baseline="none"
                              >
                                <Text
                                  size="sm"
                                  className={style({
                                    textShadow: `-1px -1px 0 rgba(220,220,220,0.3),
                                                           1px -1px 0 rgba(220,220,220,0.3),
                                                           -1px 1px 0 rgba(220,220,220,0.3),
                                                            1px 1px 0 rgba(220,220,220,0.3)`,
                                  })}
                                >
                                  {count} / {totalRows}
                                </Text>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )
            })}
          />
        </SnippetBody>
      </Snippet>
    )
  }),
)
